
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { useToast } from "@/hooks/use-toast";
import { api } from "../../api";

// Update interface to match API response
export interface WebsiteCategory {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  icon: string;
  color: string;
  is_active: boolean;
  children?: WebsiteCategory[];
}

interface NewWebsiteCategory {
  name: string;
  description?: string;
  parent_id?: string;
  icon: string;
  color: string;
}

interface UpdateWebsiteCategory {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  icon: string;
  color: string;
  is_active: boolean;
}

export const useWebsiteCategories = () => {
  const [categories, setCategories] = useState<WebsiteCategory[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const { toast } = useToast();

  // Query categories from Convex
  const categoriesData = useQuery(api.siteCategories.list.categories);
  const addCategoryMutation = useMutation(api.siteCategories.add.category);
  const updateCategoryMutation = useMutation(api.siteCategories.update.category);
  const deleteCategoryMutation = useMutation(api.siteCategories.delete.category);

  const isLoading = categoriesData === undefined;

  // Initialize database with hardcoded categories if empty
  useEffect(() => {
    if (categoriesData && !hasInitialized && Array.isArray(categoriesData) && categoriesData.length === 0) {
      setHasInitialized(true);
      
      // Add predefined categories to database
      const predefinedCategories = [
        'Arts & Entertainment',
        'Automotive',
        'Business & Economy',
        'Careers',
        'Education',
        'Family & Parenting',
        'Health & Medicine (Fitness & Wellness)',
        'Food & Drink',
        'Hobbies & Interests',
        'Home & Garden',
        'Law, Government & Politics',
        'News & Media',
        'Personal Finance',
        'Pets',
        'Religion & Spirituality',
        'Science',
        'Shopping & E-Commerce',
        'Sports',
        'Style & Fashion',
        'Technology & Computing',
        'Travel & Tourism',
        'Real Estate',
        'Social Media & Networking',
        'Blogs & Personal Sites',
        'Forums & Online Communities',
        'Dating & Relationships',
        'Adult Content',
        'Gambling',
        'Drugs, Alcohol & Tobacco',
        'Hate & Extremism',
        'Illegal Activities',
        'Malicious or Fraudulent Sites',
        'Online Services & Utilities',
        'Games',
        'Jewish News',
        'Jewish Entertainment',
        'Jewish Education',
        'Jewish Site'
      ];

      // Add each category to the database
      predefinedCategories.forEach(async (categoryName) => {
        try {
          await addCategoryMutation({ name: categoryName });
        } catch (error) {
          console.error(`Error adding category ${categoryName}:`, error);
        }
      });
    }
  }, [categoriesData, hasInitialized, addCategoryMutation]);

  // Update local categories when data changes
  useEffect(() => {
    if (categoriesData && Array.isArray(categoriesData)) {
      // Convert API response to WebsiteCategory format
      const formattedCategories: WebsiteCategory[] = categoriesData.map((name: string) => ({
        id: name.toLowerCase().replace(/[^a-z0-9]/g, ''), // Use normalized name as ID
        name,
        icon: getCategoryIcon(name),
        color: getCategoryColor(name),
        is_active: true
      }));
      setCategories(formattedCategories);
    }
  }, [categoriesData]);

  // Helper functions to get icon and color based on category name
  const getCategoryIcon = (name: string): string => {
    const iconMap: Record<string, string> = {
      'Arts & Entertainment': 'Palette',
      'Automotive': 'Car',
      'Business & Economy': 'Briefcase',
      'Careers': 'Users',
      'Education': 'GraduationCap',
      'Family & Parenting': 'Home',
      'Health & Medicine (Fitness & Wellness)': 'Heart',
      'Food & Drink': 'UtensilsCrossed',
      'Hobbies & Interests': 'Star',
      'Home & Garden': 'TreePine',
      'Law, Government & Politics': 'Scale',
      'News & Media': 'Newspaper',
      'Personal Finance': 'DollarSign',
      'Pets': 'Cat',
      'Religion & Spirituality': 'Church',
      'Science': 'Microscope',
      'Shopping & E-Commerce': 'ShoppingBag',
      'Sports': 'Trophy',
      'Style & Fashion': 'Shirt',
      'Technology & Computing': 'Monitor',
      'Travel & Tourism': 'Plane',
      'Real Estate': 'Building',
      'Social Media & Networking': 'Users',
      'Blogs & Personal Sites': 'Edit',
      'Forums & Online Communities': 'MessageSquare',
      'Dating & Relationships': 'Heart',
      'Adult Content': 'EyeOff',
      'Gambling': 'Dice6',
      'Drugs, Alcohol & Tobacco': 'AlertTriangle',
      'Hate & Extremism': 'Ban',
      'Illegal Activities': 'Shield',
      'Malicious or Fraudulent Sites': 'ShieldAlert',
      'Online Services & Utilities': 'Settings',
      'Games': 'Gamepad2',
      'Jewish News': 'Newspaper',
      'Jewish Entertainment': 'Film',
      'Jewish Education': 'BookOpen',
      'Jewish Site': 'Globe'
    };
    return iconMap[name] || 'Globe';
  };

  const getCategoryColor = (name: string): string => {
    const colorMap: Record<string, string> = {
      'Arts & Entertainment': '#8B5CF6',
      'Automotive': '#6B7280',
      'Business & Economy': '#3B82F6',
      'Careers': '#059669',
      'Education': '#7C3AED',
      'Family & Parenting': '#EC4899',
      'Health & Medicine (Fitness & Wellness)': '#10B981',
      'Food & Drink': '#F59E0B',
      'Hobbies & Interests': '#FBBF24',
      'Home & Garden': '#16A34A',
      'Law, Government & Politics': '#1F2937',
      'News & Media': '#0F172A',
      'Personal Finance': '#059669',
      'Pets': '#F97316',
      'Religion & Spirituality': '#7C2D12',
      'Science': '#0891B2',
      'Shopping & E-Commerce': '#EF4444',
      'Sports': '#16A34A',
      'Style & Fashion': '#DB2777',
      'Technology & Computing': '#6366F1',
      'Travel & Tourism': '#0EA5E9',
      'Real Estate': '#78716C',
      'Social Media & Networking': '#EC4899',
      'Blogs & Personal Sites': '#6B7280',
      'Forums & Online Communities': '#8B5CF6',
      'Dating & Relationships': '#F43F5E',
      'Adult Content': '#DC2626',
      'Gambling': '#B91C1C',
      'Drugs, Alcohol & Tobacco': '#991B1B',
      'Hate & Extremism': '#7F1D1D',
      'Illegal Activities': '#450A0A',
      'Malicious or Fraudulent Sites': '#7F1D1D',
      'Online Services & Utilities': '#374151',
      'Games': '#2563EB',
      'Jewish News': '#1D4ED8',
      'Jewish Entertainment': '#EA580C',
      'Jewish Education': '#7C3AED',
      'Jewish Site': '#059669'
    };
    return colorMap[name] || '#6B7280';
  };

  const createCategory = async (newCategory: NewWebsiteCategory) => {
    try {
      await addCategoryMutation({ name: newCategory.name });
      
      toast({
        title: "Success",
        description: "Website category created successfully",
      });
    } catch (error) {
      console.error('Error creating website category:', error);
      toast({
        title: "Error",
        description: "Failed to create website category",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateCategory = async (updatedCategory: UpdateWebsiteCategory) => {
    try {
      await updateCategoryMutation({ name: updatedCategory.name });
      
      toast({
        title: "Success",
        description: "Website category updated successfully",
      });
    } catch (error) {
      console.error('Error updating website category:', error);
      toast({
        title: "Error",
        description: "Failed to update website category",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      // Find the category name to delete
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) {
        throw new Error('Category not found');
      }
      
      await deleteCategoryMutation({ category: categoryId as any });
      
      toast({
        title: "Success",
        description: "Website category deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting website category:', error);
      toast({
        title: "Error",
        description: "Failed to delete website category",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    categories,
    isLoading,
    createCategory,
    updateCategory,
    deleteCategory
  };
};
